import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, CheckCircle2, Loader2, FileText } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { COUNTRY_CODES } from "./RfqWizard";
import { useSiteContent } from "../../context/SiteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_MODELS = {
  "Yantr 2.0 Series": "/datasheets/yantr-2.pdf",
  "Yantr 3.0 Series": "/datasheets/yantr-3.pdf",
};

export const DatasheetModal = ({ open, onClose, model }) => {
  const { content } = useSiteContent();
  const MODELS = React.useMemo(() => {
    const m = {};
    (content?.productSeries || []).forEach((s) => {
      const pdf = (s.models || [])[0]?.datasheetPdfUrl;
      if (pdf) m[s.seriesName] = pdf;
    });
    return Object.keys(m).length ? m : FALLBACK_MODELS;
  }, [content]);
  const firstModel = Object.keys(MODELS)[0] || "Yantr 2.0 Series";
  const [form, setForm] = useState({ name: "", countryCode: "+91", mobile: "", email: "", model: firstModel });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setDone(false);
      setForm((f) => ({ ...f, model: model || firstModel }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, model]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const triggerDownload = () => {
    const url = MODELS[form.model] || Object.values(MODELS)[0];
    const a = document.createElement("a");
    a.href = url;
    a.download = url.split("/").pop();
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.mobile) {
      return toast.error("Please fill in your name, WhatsApp number and email.");
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        phone: `${form.countryCode} ${form.mobile}`,
        company: "",
        message: `Datasheet download — ${form.model}`,
        lead_type: "datasheet",
        details: { "Model": form.model },
      });
      triggerDownload();
      setDone(true);
      toast.success("Datasheet downloading — check your downloads folder.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="datasheet-modal">
          <div className="absolute inset-0 bg-helios-slate/50 backdrop-blur-sm" onClick={onClose} data-testid="datasheet-overlay" />
          <motion.div className="relative z-10 w-full max-w-md rounded-2xl bg-white border border-helios-line shadow-2xl"
            initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}>
            <button onClick={onClose} className="absolute right-4 top-4 text-slate-400 hover:text-helios-slate transition-colors" data-testid="datasheet-close">
              <X size={20} />
            </button>

            {done ? (
              <div className="p-9 text-center">
                <CheckCircle2 size={48} className="mx-auto text-helios-cyan" />
                <h3 className="font-display text-xl font-bold mt-4 text-helios-slate">Your download is ready</h3>
                <p className="text-slate-500 mt-2 text-sm leading-relaxed">
                  The {form.model} datasheet is downloading. Didn't start?
                </p>
                <button onClick={triggerDownload} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-helios-cyan" data-testid="datasheet-redownload">
                  <Download size={15} /> Download again
                </button>
                <div>
                  <Button onClick={onClose} className="mt-5 rounded-full bg-helios-slate hover:bg-helios-slate/90 px-8" data-testid="datasheet-done">Close</Button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="p-8">
                <div className="flex items-center gap-2 text-helios-cyan mb-1.5">
                  <FileText size={16} /><p className="overline">Technical Datasheet</p>
                </div>
                <h3 className="font-display text-xl font-bold text-helios-slate">Get the {form.model} datasheet</h3>
                <p className="text-sm text-slate-500 mt-1.5 mb-5">Enter your details to download the full spec sheet.</p>

                <div className="space-y-4">
                  <div>
                    <span className="block text-xs font-medium text-slate-500 mb-1.5">Model</span>
                    <select value={form.model} onChange={upd("model")}
                      className="w-full rounded-lg border border-helios-line bg-white px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-helios-cyan"
                      data-testid="datasheet-model">
                      {Object.keys(MODELS).map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <Input placeholder="Full name" value={form.name} onChange={upd("name")} data-testid="datasheet-name" className="rounded-lg border-helios-line" />
                  <div className="flex gap-2">
                    <select value={form.countryCode} onChange={upd("countryCode")}
                      className="rounded-lg border border-helios-line bg-white px-2 text-sm h-10 focus:outline-none focus:ring-2 focus:ring-helios-cyan"
                      data-testid="datasheet-country-code">
                      {COUNTRY_CODES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                    </select>
                    <Input className="rounded-lg border-helios-line flex-1" placeholder="WhatsApp number"
                      value={form.mobile} onChange={upd("mobile")} data-testid="datasheet-mobile" />
                  </div>
                  <Input type="email" placeholder="Work email address" value={form.email} onChange={upd("email")} data-testid="datasheet-email" className="rounded-lg border-helios-line" />
                </div>

                <Button type="submit" disabled={loading}
                  className="btn-sheen mt-6 w-full rounded-full bg-helios-slate hover:bg-helios-slate/90 h-12"
                  data-testid="datasheet-submit">
                  {loading ? <Loader2 className="animate-spin" size={18} /> : <><Download size={16} className="mr-2" /> Download Datasheet</>}
                </Button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
